import type { Contact, Supplier } from 'features/suppliers/types';
import { getSuppliersData } from 'mocks/api/handlers/suppliersCache';

/**
 * Deterministic, additive seed for the "Update Supplier: leaving edit mode and the post-save
 * view" e2e epic (DL-66006). Appends one dedicated row per story to org 2's (brand `page`
 * fixture) cache — same convention as the sibling `supplierEditEntryFixture.ts` (DL-65988) and
 * `supplierEditValidationFixture.ts` (DL-65996). Distinct ids/sub-org-ids (900301-900310) from
 * both sibling epics' ranges (900101-900103, 900201-900209).
 *
 * Every mutating story gets its own dedicated row so a PUT one test issues can never be observed
 * by another test (same rationale as the DL-65996 fixture's header).
 *
 * Duplicated by value in `e2e/src/commons/suppliers/supplierEditExit.helpers.ts` (e2e has no path
 * alias into `frontend/src` — established precedent, see the sibling fixtures' headers).
 */
const SUPPLIER_EDIT_ORG_ID = 2;

export const SUPPLIER_EXIT_DISCARD_ID = 'e2e-supplier-exit-discard';
export const SUPPLIER_EXIT_DISCARD_SUB_ORG_ID = 900301;
export const SUPPLIER_EXIT_DISCARD_NAME = 'Fenwick Cotton Traders';

export const SUPPLIER_EXIT_DIALOG_ID = 'e2e-supplier-exit-dialog';
export const SUPPLIER_EXIT_DIALOG_SUB_ORG_ID = 900302;
export const SUPPLIER_EXIT_DIALOG_NAME = 'Alderwood Fastener Supply';

export const SUPPLIER_EXIT_TOAST_ID = 'e2e-supplier-exit-toast';
export const SUPPLIER_EXIT_TOAST_SUB_ORG_ID = 900303;
export const SUPPLIER_EXIT_TOAST_NAME = 'Brightwater Notions Co';

export const SUPPLIER_EXIT_POSTSAVE_ID = 'e2e-supplier-exit-postsave';
export const SUPPLIER_EXIT_POSTSAVE_SUB_ORG_ID = 900304;
export const SUPPLIER_EXIT_POSTSAVE_NAME = 'Kestrel Print Works';

export const SUPPLIER_EXIT_BACKTOLIST_ID = 'e2e-supplier-exit-backtolist';
export const SUPPLIER_EXIT_BACKTOLIST_SUB_ORG_ID = 900305;
export const SUPPLIER_EXIT_BACKTOLIST_NAME = 'Thistledown Zipper Co';
export const SUPPLIER_EXIT_BACKTOLIST_CUSTOM_ID = 'SUP-9305';

export const SUPPLIER_EXIT_CLEAN_ID = 'e2e-supplier-exit-clean';
export const SUPPLIER_EXIT_CLEAN_SUB_ORG_ID = 900306;
export const SUPPLIER_EXIT_CLEAN_NAME = 'Grayling Wool Exchange';

export const SUPPLIER_EXIT_DISABLED_ID = 'e2e-supplier-exit-disabled';
export const SUPPLIER_EXIT_DISABLED_SUB_ORG_ID = 900307;
export const SUPPLIER_EXIT_DISABLED_NAME = 'Hollowbrook Elastics Ltd';

/** Distinguishable, non-symmetric coordinates (epic divergence 5) so a longitude/latitude order
 *  regression actually fails the spec. */
export const SUPPLIER_EXIT_GPS_FULL_ID = 'e2e-supplier-exit-gps-full';
export const SUPPLIER_EXIT_GPS_FULL_SUB_ORG_ID = 900308;
export const SUPPLIER_EXIT_GPS_FULL_NAME = 'Amberfield Trim Supply';
export const SUPPLIER_EXIT_GPS_FULL_LONGITUDE = 25.1234;
export const SUPPLIER_EXIT_GPS_FULL_LATITUDE = 61.9876;

/** No coordinates at all (fields omitted, i.e. `undefined`) — the `??` fallback in
 *  `SupplierBasicInfo.tsx` treats `undefined` the same as `null`, so this exercises the same
 *  fallback path without widening the `Supplier` type. */
export const SUPPLIER_EXIT_GPS_EMPTY_ID = 'e2e-supplier-exit-gps-empty';
export const SUPPLIER_EXIT_GPS_EMPTY_SUB_ORG_ID = 900309;
export const SUPPLIER_EXIT_GPS_EMPTY_NAME = 'Solholm Ribbon Works';

export const SUPPLIER_EXIT_SCROLL_ID = 'e2e-supplier-exit-scroll';
export const SUPPLIER_EXIT_SCROLL_SUB_ORG_ID = 900310;
export const SUPPLIER_EXIT_SCROLL_NAME = 'Vantage Leather Guild';

const baseContact = (seed: number, overrides: Partial<Contact> = {}): Contact => ({
  id: seed,
  firstName: 'Nils',
  lastName: 'Andersen',
  email: `contact${seed}@example.com`,
  phone: '+4511223344',
  role: 'Sourcing Manager',
  isPrimaryContact: true,
  ...overrides,
});

const makeSupplier = (
  id: string,
  organizationId: number,
  name: string,
  overrides: Partial<Supplier> = {}
): Supplier => ({
  id,
  organizationId,
  name,
  relationType: 2,
  customId: `SUP-${organizationId}`,
  mid: `MID${organizationId}`,
  address: '1 Fixture Lane',
  zipCode: '4000',
  city: 'Odense',
  stateOrRegion: 'Funen',
  countryCode: 'DK',
  latitude: 55.4038,
  longitude: 10.4024,
  contacts: [baseContact(organizationId * 10 + 1)],
  facilities: [],
  archived: false,
  isArchived: false,
  isSupplierInUse: false,
  ...overrides,
});

const SUPPLIER_EXIT_DISCARD = makeSupplier(
  SUPPLIER_EXIT_DISCARD_ID,
  SUPPLIER_EXIT_DISCARD_SUB_ORG_ID,
  SUPPLIER_EXIT_DISCARD_NAME
);

const SUPPLIER_EXIT_DIALOG = makeSupplier(
  SUPPLIER_EXIT_DIALOG_ID,
  SUPPLIER_EXIT_DIALOG_SUB_ORG_ID,
  SUPPLIER_EXIT_DIALOG_NAME
);

const SUPPLIER_EXIT_TOAST = makeSupplier(
  SUPPLIER_EXIT_TOAST_ID,
  SUPPLIER_EXIT_TOAST_SUB_ORG_ID,
  SUPPLIER_EXIT_TOAST_NAME
);

const SUPPLIER_EXIT_POSTSAVE = makeSupplier(
  SUPPLIER_EXIT_POSTSAVE_ID,
  SUPPLIER_EXIT_POSTSAVE_SUB_ORG_ID,
  SUPPLIER_EXIT_POSTSAVE_NAME
);

const SUPPLIER_EXIT_BACKTOLIST = makeSupplier(
  SUPPLIER_EXIT_BACKTOLIST_ID,
  SUPPLIER_EXIT_BACKTOLIST_SUB_ORG_ID,
  SUPPLIER_EXIT_BACKTOLIST_NAME,
  { customId: SUPPLIER_EXIT_BACKTOLIST_CUSTOM_ID }
);

const SUPPLIER_EXIT_CLEAN = makeSupplier(
  SUPPLIER_EXIT_CLEAN_ID,
  SUPPLIER_EXIT_CLEAN_SUB_ORG_ID,
  SUPPLIER_EXIT_CLEAN_NAME
);

const SUPPLIER_EXIT_DISABLED = makeSupplier(
  SUPPLIER_EXIT_DISABLED_ID,
  SUPPLIER_EXIT_DISABLED_SUB_ORG_ID,
  SUPPLIER_EXIT_DISABLED_NAME
);

const SUPPLIER_EXIT_GPS_FULL = makeSupplier(
  SUPPLIER_EXIT_GPS_FULL_ID,
  SUPPLIER_EXIT_GPS_FULL_SUB_ORG_ID,
  SUPPLIER_EXIT_GPS_FULL_NAME,
  { longitude: SUPPLIER_EXIT_GPS_FULL_LONGITUDE, latitude: SUPPLIER_EXIT_GPS_FULL_LATITUDE }
);

const SUPPLIER_EXIT_GPS_EMPTY = makeSupplier(
  SUPPLIER_EXIT_GPS_EMPTY_ID,
  SUPPLIER_EXIT_GPS_EMPTY_SUB_ORG_ID,
  SUPPLIER_EXIT_GPS_EMPTY_NAME,
  { longitude: undefined, latitude: undefined }
);

/** Six additional contacts (plus the default primary) so the Basic-information pane overflows a
 *  1920x1024 viewport's content region — enough for the sticky-header scroll story (DL-66014). */
const SUPPLIER_EXIT_SCROLL = makeSupplier(
  SUPPLIER_EXIT_SCROLL_ID,
  SUPPLIER_EXIT_SCROLL_SUB_ORG_ID,
  SUPPLIER_EXIT_SCROLL_NAME,
  {
    contacts: [
      baseContact(9031001, { isPrimaryContact: true, firstName: 'Primary', lastName: 'Contact' }),
      ...Array.from({ length: 6 }, (_, i) =>
        baseContact(9031002 + i, {
          isPrimaryContact: false,
          firstName: `Additional${i + 1}`,
          lastName: 'Contact',
          role: 'Quality Lead',
        })
      ),
    ],
  }
);

let seeded = false;

/** Idempotent: appends the ten deterministic rows to org 2's cached supplier list. */
export const seedSupplierEditExitFixture = (): void => {
  if (seeded) return;
  seeded = true;

  const data = getSuppliersData(SUPPLIER_EDIT_ORG_ID);
  data.push(
    SUPPLIER_EXIT_DISCARD,
    SUPPLIER_EXIT_DIALOG,
    SUPPLIER_EXIT_TOAST,
    SUPPLIER_EXIT_POSTSAVE,
    SUPPLIER_EXIT_BACKTOLIST,
    SUPPLIER_EXIT_CLEAN,
    SUPPLIER_EXIT_DISABLED,
    SUPPLIER_EXIT_GPS_FULL,
    SUPPLIER_EXIT_GPS_EMPTY,
    SUPPLIER_EXIT_SCROLL
  );
};
